import { Loading } from '@/components';
import api, { AxiosError } from '@/services/api';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import EventMainTrackingReport from '@/model/EventMainTrackingReport';
import Page from '@/model/Page';
import EventTrackingReport from '@/model/EventTrackingReport';
import { EventFilterTrackingReport } from '@/model/EventFilterTracking';
import useForm from '@/hooks/useForm';
// import { updateMask as updateMaskDate } from '@/helpers/masks/generalDate';
import Section from '@/model/Section';
import { AuthUser } from '@/model/AuthUser';
import { REACT_APP_USER } from '@/utils/config';
import { getItem } from '@/helpers/common/localStorage';
import { TrackingContent } from './ui';
import { FormInputFilterTracking, OptionsPdv, ReportControllerProps } from '../../types';

export interface TrackingProps {
  generatePDF: () => void;
  reportController: ReportControllerProps;
}

// eslint-disable-next-line no-shadow
export enum States {
  default = 'default',
  loading = 'loading',
}

const EMPTY_FILTERS: EventFilterTrackingReport = {
  pdvIds: [],
  orderDateStart: undefined,
  orderDateEnd: undefined,
  paymentTypes: [],
  sections: [],
  itemLabel: '',
  isSite: false,
};

export const Tracking: React.FC<TrackingProps> = ({ reportController, generatePDF }) => {
  const [state, setState] = useState<States>(States.default);
  const [trackingPagination, setTrackingPagination] = useState<
    Page<EventTrackingReport, EventTrackingReport>
  >({
    page: 1,
    pageSize: 10,
    total: 0,
  });
  const [pdvs, setPdvs] = useState<OptionsPdv[]>([]);
  const [selectPdvs, setSelectPdvs] = useState<OptionsPdv[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [selectSection, setSelectSection] = useState<Section[]>([]);
  const [tracking, setTracking] = useState<EventMainTrackingReport>();
  const [isFilter, setIsFilter] = useState<boolean>(false);
  const user = getItem(String(REACT_APP_USER)) as AuthUser;

  const {
    eventId,
    singleEvent,
    // tracking,
    // setTracking,
    // trackingPagination,
    // setTrackingPagination,
    // onPageTracking,
  } = reportController;

  const {
    formData: formDataFilters,
    formErrors: formErrorsFilters,
    onChangeFormInput: onChangeFormInputFilters,
    resetForm: resetFormFilters,
  } = useForm({
    initialData: {
      orderDateStart: '',
      orderDateEnd: '',
      itemLabel: '',
    },
    // formatters: {
    //   orderDateStart: updateMaskDate,
    //   orderDateEnd: updateMaskDate,
    // },
  });

  const onPageTracking = async (
    page: number,
    filters?: EventFilterTrackingReport,
  ): Promise<void> => {
    try {
      setState(States.loading);

      const newPage = {
        ...trackingPagination,
        page,
        entity: { filters },
      };
      const { data } = await api.post<Page<EventTrackingReport, EventTrackingReport>>(
        `event/report/${eventId}/tracking?singleEvent=${singleEvent}`,
        newPage,
      );

      if (data) {
        setTrackingPagination(data);
      }
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  const getPdvs = async (): Promise<void> => {
    try {
      setState(States.loading);
      const { data } = await api.get<OptionsPdv[]>(`event/report/${eventId}/pdv-tracking`);
      const withSite = [{ pdvID: '1', pdvName: 'Site' }, ...data];
      setPdvs(withSite);
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  const handleGetTracking = async (filter: EventFilterTrackingReport): Promise<void> => {
    try {
      setState(States.loading);
      const payload = {
        filter,
      };
      const { data } = await api.post<EventMainTrackingReport>(
        `event/report/${eventId}/tracking-summary?singleEvent=${singleEvent}`,
        payload,
      );
      if (data) {
        setTracking(data);
        if (tracking === undefined) {
          const newPage: Page<EventTrackingReport, EventTrackingReport> = {
            page: 1,
            pageSize: 10,
            list: data.trackings,
            total: data.total,
          };
          setTrackingPagination(newPage);
          if (data && data.sections) {
            setSections(data.sections);
          }

          resetFormFilters();
        }
      }
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  const handleOnChangeFilters = (idPdv: string): void => {
    const exists = selectPdvs.some(item => item.pdvID === idPdv);
    if (!exists) {
      const found = pdvs.find(pdv => pdv.pdvID === idPdv);
      if (found) {
        setSelectPdvs(prev => [...prev, found]);
      }
    } else {
      setSelectPdvs(prev => prev.filter(pdv => pdv.pdvID !== idPdv));
    }
  };

  const handleOnChangeSection = (idSection: string): void => {
    const exists = selectSection.some(item => item.id === idSection);
    if (!exists) {
      const found = sections.find(section => section.id === idSection);
      if (found) {
        setSelectSection(prev => [...prev, found]);
      }
    } else {
      setSelectSection(prev => prev.filter(section => section.id !== idSection));
    }
  };

  const handleFilter = (page: number): void => {
    const startDate = formDataFilters[FormInputFilterTracking.orderDateStart];
    const endDate = formDataFilters[FormInputFilterTracking.orderDateEnd];
    let pdvIds: string[] = [];
    let isSite = false;
    let sectionsId: string[] = [];
    if (selectPdvs.some(pdv => pdv.pdvID !== '1')) {
      isSite = true;
    }
    if (selectPdvs.some(pdv => pdv.pdvID !== '1')) {
      const newPdvs = selectPdvs.filter(pdv => pdv.pdvID !== '1');
      pdvIds = newPdvs.map(pdv => pdv.pdvID);
      isSite = false;
    }
    if (selectSection.length > 0) {
      sectionsId = selectSection.map(section => section.id);
    }
    const newFilters: EventFilterTrackingReport = {
      pdvIds: pdvIds || [],
      orderDateStart: startDate || undefined,
      orderDateEnd: endDate || undefined,
      paymentTypes: [],
      sections: sectionsId,
      itemLabel: formDataFilters[FormInputFilterTracking.itemLabel],
      isSite,
    };

    onPageTracking(page, newFilters);
    handleGetTracking(newFilters);
  };

  const handleOnFilter = (): void => {
    setIsFilter(!isFilter);
  };

  const handleClearFilters = (): void => {
    handleGetTracking(EMPTY_FILTERS);
    onPageTracking(1, EMPTY_FILTERS);
    setSelectPdvs([]);
    setSelectSection([]);
    resetFormFilters();
  };

  useEffect(() => {
    handleGetTracking(EMPTY_FILTERS);
    setSelectPdvs([]);
    setSelectSection([]);
    resetFormFilters();
  }, [eventId]);

  useEffect(() => {
    getPdvs();
  }, []);

  return (
    <>
      <Loading isVisible={state === 'loading'} />
      <TrackingContent
        tracking={tracking}
        generatePDF={generatePDF}
        pagination={trackingPagination}
        pdvs={pdvs}
        selectPdvs={selectPdvs}
        formData={formDataFilters}
        formErrors={formErrorsFilters}
        onChangeFormInput={onChangeFormInputFilters}
        onChangeFilters={handleOnChangeFilters}
        clearFilters={handleClearFilters}
        submitFilter={handleFilter}
        sections={sections}
        selectSection={selectSection}
        onChangeSection={handleOnChangeSection}
        onFilter={handleOnFilter}
        isFilter={isFilter}
        userType={user.userType}
      />
    </>
  );
};
